// src/components/Login.js
import React, { useState } from "react";
import axios from "axios";
import { verificarCredenciales } from "../services/authService";
import { obtenerEmpresa } from "../services/empresaService";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const EyeIcon = ({ open }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
        <line x1="1" y1="1" x2="23" y2="23"/>
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
        <circle cx="12" cy="12" r="3"/>
      </>
    )}
  </svg>
);

export default function Login({ onLoginSuccess }) {
  const [clave, setClave]           = useState("");
  const [password, setPassword]     = useState("");
  const [empresas, setEmpresas]     = useState([]);
  const [empresaId, setEmpresaId]   = useState("");
  const [showPass, setShowPass]     = useState(false);
  const [loading, setLoading]       = useState(false);
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [error, setError]           = useState(
    () => { const m = sessionStorage.getItem("loginNotice"); sessionStorage.removeItem("loginNotice"); return m ?? ""; }
  );

  const pickId = (obj) => {
    const candidates = [obj.id, obj.ID, obj.Id, obj.empresaId, obj.EmpresaId, obj.empresa, obj.Empresa];
    const hit = candidates.find((v) => v !== undefined && v !== null);
    if (hit !== undefined) return hit;
    const k = Object.keys(obj).find((k) => /(^id$|empresa.*id$|^id.*empresa$|^empresa$)/i.test(k));
    return k ? obj[k] : null;
  };

  const pickNombre = (obj) => {
    const candidates = [obj.nombre, obj.Nombre, obj.razonSocial, obj.RazonSocial, obj.nombreEmpresa, obj.NombreEmpresa, obj.descripcion, obj.Descripcion];
    const hit = candidates.find((v) => typeof v === "string" && v.trim());
    if (hit) return hit;
    const k = Object.keys(obj).find((k) => /(nombre.*empresa|nombre|razon)/i.test(k));
    return k && typeof obj[k] === "string" ? obj[k] : "Empresa";
  };

  const cargarEmpresas = async (claveFiltro = "") => {
    try {
      setLoadingEmpresas(true);
      const params = claveFiltro.trim()
        ? { clave: claveFiltro.trim() }
        : { soloActivas: true };
      const url = claveFiltro.trim()
        ? `${API_BASE}/api/auth/empresas-usuario`
        : `${API_BASE}/api/empresas`;
      const { data } = await axios.get(url, { params });
      const raw = Array.isArray(data?.data) ? data.data : Array.isArray(data?.Data) ? data.Data : Array.isArray(data) ? data : [];
      const mapped = raw.map((e) => ({ id: pickId(e), nombre: pickNombre(e) })).filter((x) => x.id != null);
      setEmpresas(mapped);
      if (mapped.length === 1) {
        setEmpresaId(String(mapped[0].id));
      } else {
        const saved = parseInt(localStorage.getItem("empresaId") || "", 10);
        if (Number.isFinite(saved) && mapped.some((m) => Number(m.id) === saved)) {
          setEmpresaId(String(saved));
        } else {
          setEmpresaId("");
        }
      }
    } catch (err) {
      console.error("Error cargando empresas:", err?.response?.data ?? err);
      setError("No se pudieron cargar las empresas.");
    } finally {
      setLoadingEmpresas(false);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!clave.trim())   { setError("La clave de usuario es obligatoria."); return; }
    if (!password.trim()){ setError("La contraseña es obligatoria."); return; }
    if (!empresaId)      { setError("Selecciona una empresa."); return; }

    try {
      setLoading(true);
      const resp = await verificarCredenciales(clave.trim(), password, parseInt(empresaId, 10));
      const user = resp?.data ?? {};
      localStorage.setItem("auth", "1");
      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("empresaId", String(empresaId));
      // Dispara el aviso de cotizaciones "por facturar" vencidas una sola vez, justo al entrar.
      sessionStorage.setItem("avisoPorFacturarPendiente", "1");

      try {
        const empRaw = await obtenerEmpresa(empresaId);
        const empresaObj = Array.isArray(empRaw) ? empRaw[0] : empRaw;
        localStorage.setItem("empresa", JSON.stringify(empresaObj));
      } catch { localStorage.removeItem("empresa"); }

      onLoginSuccess?.();
    } catch (err) {
      const d = err?.response?.data;
      setError(d?.title || d?.message || d || "Credenciales invalidas.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lmd-login-wrap">
      {/* Decoracion de fondo */}
      <div style={{
        position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none",
        background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #0f172a 100%)",
      }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{
            position: "absolute",
            width: `${180 + i * 80}px`, height: `${180 + i * 80}px`,
            borderRadius: "50%",
            border: "1px solid rgba(59,130,246,.08)",
            top: `${10 + i * 8}%`, left: `${5 + i * 5}%`,
          }} />
        ))}
      </div>

      <div className="lmd-login-card">
        {/* Logo */}
        <div className="lmd-login-logo-wrap">
          <div className="lmd-login-logo">L</div>
        </div>

        <h1 className="lmd-login-title">LMD Integradora</h1>
        <p className="lmd-login-subtitle">Inicia sesion para continuar</p>

        {error && (
          <div style={{
            background: "var(--clr-danger-bg)", color: "var(--clr-danger)",
            border: "1px solid #fecaca", borderRadius: "var(--radius-md)",
            padding: "10px 14px", fontSize: "var(--fs-sm)",
          }}>
            {error}
          </div>
        )}

        <form className="lmd-login-form" onSubmit={handleSubmit}>
          {/* Clave */}
          <div className="lmd-field">
            <label className="lmd-label">Clave de usuario</label>
            <input
              className="lmd-input"
              placeholder="ej. admin"
              value={clave}
              onChange={(e) => setClave(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim()) cargarEmpresas(e.target.value.trim()); }}
              autoFocus
              autoComplete="username"
            />
          </div>

          {/* Password */}
          <div className="lmd-field">
            <label className="lmd-label">Contraseña</label>
            <div style={{ position: "relative" }}>
              <input
                className="lmd-input"
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{ paddingRight: 44 }}
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  border: "none", background: "transparent", cursor: "pointer",
                  color: "var(--clr-text-muted)", display: "grid", placeItems: "center", padding: 4,
                }}
              >
                <EyeIcon open={showPass} />
              </button>
            </div>
          </div>

          {/* Empresa */}
          <div className="lmd-field">
            <label className="lmd-label">Empresa</label>
            <select
              className="lmd-input"
              value={empresaId}
              onChange={(e) => setEmpresaId(e.target.value)}
              disabled={loadingEmpresas}
              style={{ cursor: loadingEmpresas ? "not-allowed" : "pointer" }}
            >
              <option value="">{loadingEmpresas ? "Cargando empresas..." : "Selecciona una empresa"}</option>
              {empresas.map((e) => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
            </select>
          </div>

          <button
            className="lmd-login-btn"
            type="submit"
            disabled={loading || loadingEmpresas}
          >
            {loading ? "Iniciando sesion..." : "Iniciar Sesion"}
          </button>
        </form>

        <div className="lmd-login-footer">
          © {new Date().getFullYear()} LMD Integradora · Todos los derechos reservados
        </div>
      </div>
    </div>
  );
}
